/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Directive,
  Input,
  OnInit,
  ElementRef,
  Renderer2,
  EventEmitter,
  Output,
} from '@angular/core';

@Directive({
  selector: 'th[sortable]',
  host: {
    '(click)': 'action()',
  },
  standalone: false,
})
export class TableSort implements OnInit {
  @Input() sortable = '';
  @Input() direction: SortDirection = '';
  @Input() data: never[];
  @Input() sortType: SortType = '';
  @Output() dataChange = new EventEmitter<never[]>();

  child: HTMLElement;
  rotate: Record<string, SortDirection> = {
    asc: 'desc',
    desc: 'asc',
    '': 'asc',
  };

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.appendChild();
  }

  appendChild() {
    // create and append the i tag that contains the sorting glyph to the the th tag
    this.child = this.renderer.createElement('i');
    this.renderer.setAttribute(this.child, 'aria-hidden', 'true');
    this.renderer.addClass(this.child, 'sort-glyph');
    this.renderer.addClass(this.child, 'xgl-sort_down');
    this.renderer.appendChild(this.elementRef.nativeElement, this.child);
  }

  action() {
    this.direction = this.rotate[this.direction];
    this.updateStyle();
    this.sortData();
  }

  updateStyle() {
    // remove 'sort-active' class from all sibling th tags
    [...this.elementRef.nativeElement.parentElement.children].forEach((sib) =>
      sib.classList.remove('sort-active'),
    );
    // add 'sort-active' class to active th tag
    this.elementRef.nativeElement.classList.add('sort-active');
    // set correct glyph based on sorting direction
    if (this.direction === 'asc') {
      this.renderer.removeClass(this.child, 'xgl-sort_up');
      this.renderer.addClass(this.child, 'xgl-sort_down');
    } else if (this.direction === 'desc') {
      this.renderer.removeClass(this.child, 'xgl-sort_down');
      this.renderer.addClass(this.child, 'xgl-sort_up');
    } else {
      this.renderer.removeClass(this.child, 'xgl-sort_up');
      this.renderer.addClass(this.child, 'xgl-sort_down');
    }
  }

  sortData() {
    const compare = (
      v1: string | number | null,
      v2: string | number | null,
    ) => {
      if (
        this.sortType === 'number' &&
        typeof v1 === 'number' &&
        typeof v2 === 'number'
      ) {
        return v1 - v2;
      } else {
        if (v1 == null || v1 == 'null') {
          return 1;
        } else if (v2 == null || v2 == 'null') {
          return -1;
        } else {
          return String(v1).localeCompare(String(v2));
        }
      }
    };

    this.data = this.data.sort((a, b) => {
      const res = compare(`${a[this.sortable]}`, `${b[this.sortable]}`);
      return this.direction === 'asc' ? res : -res;
    });
  }
}

type SortDirection = 'asc' | 'desc' | '';
type SortType = 'string' | 'number' | '';
